<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>充值卡管理</h1>
        <p>兑换码的生成、发布与失效，一张卡可被多人兑换</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增充值卡
        </el-button>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.code"
        placeholder="搜索兑换码"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.status" placeholder="全部状态" clearable class="status-select" @change="handleFilter">
        <el-option label="未发布" :value="0" />
        <el-option label="已发布" :value="1" />
        <el-option label="已失效" :value="2" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 张</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column label="兑换码" min-width="200">
          <template slot-scope="{ row }">
            <span class="tag is-mono code-tag">{{ row.code }}</span>
          </template>
        </el-table-column>
        <el-table-column label="兑换金额" width="150">
          <template slot-scope="{ row }">
            <div class="bean-cell">
              <b>{{ toBean(row.amount) }}</b> 爱豆
            </div>
            <div class="cell-sub">¥{{ Number(row.amount || 0).toFixed(2) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="兑换进度" width="160">
          <template slot-scope="{ row }">
            <div class="use-progress">
              <b>{{ row.usedCount || 0 }}</b>
              <span>/ {{ row.maxUseCount }}</span>
            </div>
            <div class="use-bar">
              <i :style="{ width: usePercent(row) + '%' }" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="statusClass(row.status)">
              <i />{{ getStatusText(row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button
                v-if="row.usedCount > 0"
                type="button"
                class="act act-plain"
                @click="handleViewUsers(row)"
              >
                查看用户
              </button>
              <el-dropdown trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  操作<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="row.status === 0" command="publish">
                    <i class="el-icon-upload2" /> 发布
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.status === 1" command="invalidate">
                    <i class="el-icon-remove-outline" /> 失效
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.status === 0" command="edit">
                    <i class="el-icon-edit" /> 编辑
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.status === 0" command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form ref="form" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="兑换金额" prop="amount">
          <el-input-number v-model="form.amount" :min="0.01" :max="10000" :precision="2" :step="0.01" style="width: 100%;" />
          <div class="form-hint">按 ¥1 = {{ beanRate }} 爱豆记账，用户到账 {{ toBean(form.amount) }} 爱豆</div>
        </el-form-item>
        <el-form-item label="最大兑换次数" prop="maxUseCount">
          <el-input-number v-model="form.maxUseCount" :min="1" :max="1000" style="width: 100%;" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog title="已兑换用户" :visible.sync="usersDialogVisible" width="800px">
      <el-table v-loading="usersLoading" :data="usersList" style="width: 100%">
        <el-table-column prop="username" label="用户名" width="150" />
        <el-table-column prop="nickname" label="昵称" width="140" />
        <el-table-column label="兑换金额" width="150" align="right">
          <template slot-scope="{ row }">
            <span class="cell-main">{{ toBean(row.amount) }} 爱豆</span>
          </template>
        </el-table-column>
        <el-table-column label="兑换时间">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.redeemedAt) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-show="usersTotal > 0"
        background
        :current-page="usersListQuery.page"
        :page-size="usersListQuery.size"
        :page-sizes="[10, 20, 50]"
        :total="usersTotal"
        layout="total, sizes, prev, pager, next, jumper"
        class="users-pager"
        @size-change="handleUsersSizeChange"
        @current-change="handleUsersCurrentChange"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getRechargeCardList, createRechargeCard, updateRechargeCard, deleteRechargeCard, publishRechargeCard, invalidateRechargeCard, getRechargeCardUsers } from '@/api/recharge'
import { toBean, getBeanRate } from '@/utils/bean'

export default {
  name: 'RechargeCard',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        size: 10,
        code: '',
        status: ''
      },
      dialogVisible: false,
      dialogTitle: '新增充值卡',
      form: {
        id: null,
        amount: 10,
        maxUseCount: 1
      },
      rules: {
        amount: [
          { required: true, message: '请输入兑换金额', trigger: 'blur' },
          { type: 'number', min: 0.01, message: '兑换金额不能小于0.01元', trigger: 'blur' }
        ],
        maxUseCount: [
          { required: true, message: '请输入最大兑换次数', trigger: 'blur' },
          { type: 'number', min: 1, message: '兑换次数不能小于1', trigger: 'blur' }
        ]
      },
      usersDialogVisible: false,
      usersLoading: false,
      usersList: [],
      usersTotal: 0,
      usersListQuery: {
        page: 1,
        size: 10
      },
      currentCardId: null
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    beanRate() {
      return getBeanRate()
    }
  },
  methods: {
    toBean(yuan) {
      return toBean(yuan)
    },
    fetchData() {
      this.listLoading = true
      getRechargeCardList(this.listQuery).then(res => {
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.listLoading = false
      })
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    resetFilter() {
      this.listQuery.code = ''
      this.listQuery.status = ''
      this.handleFilter()
    },
    handleAction(cmd, row) {
      if (cmd === 'publish') return this.handlePublish(row)
      if (cmd === 'invalidate') return this.handleInvalidate(row)
      if (cmd === 'edit') return this.handleEdit(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },
    usePercent(row) {
      const max = Number(row.maxUseCount) || 0
      if (!max) return 0
      const pct = (Number(row.usedCount) || 0) / max * 100
      return Math.min(100, Math.round(pct))
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleCreate() {
      this.dialogTitle = '新增充值卡'
      this.form = {
        id: null,
        amount: 10,
        maxUseCount: 1
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogTitle = '编辑充值卡'
      this.form = {
        id: row.id,
        amount: row.amount,
        maxUseCount: row.maxUseCount
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (valid) {
          const formData = {
            amount: Number(this.form.amount),
            maxUseCount: Number(this.form.maxUseCount)
          }
          if (this.form.id) {
            updateRechargeCard({
              ...this.form,
              amount: Number(this.form.amount),
              maxUseCount: Number(this.form.maxUseCount)
            }).then(res => {
              this.$message.success('更新成功')
              this.dialogVisible = false
              this.fetchData()
            })
          } else {
            createRechargeCard(formData).then(res => {
              this.$message.success('创建成功')
              this.dialogVisible = false
              this.fetchData()
            })
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm('确认删除该充值卡？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteRechargeCard(row.id).then(res => {
          this.$message.success('删除成功')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handlePublish(row) {
      this.$confirm('确认发布该充值卡？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        publishRechargeCard(row.id).then(res => {
          this.$message.success('发布成功')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleInvalidate(row) {
      this.$confirm('确认失效该充值卡？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        invalidateRechargeCard(row.id).then(res => {
          this.$message.success('已失效')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleViewUsers(row) {
      this.currentCardId = row.id
      this.usersListQuery.page = 1
      this.usersDialogVisible = true
      this.fetchUsers()
    },
    fetchUsers() {
      this.usersLoading = true
      getRechargeCardUsers(this.currentCardId, this.usersListQuery).then(res => {
        this.usersList = res.data.list || []
        this.usersTotal = res.data.total || 0
        this.usersLoading = false
      })
    },
    handleUsersSizeChange(val) {
      this.usersListQuery.size = val
      this.fetchUsers()
    },
    handleUsersCurrentChange(val) {
      this.usersListQuery.page = val
      this.fetchUsers()
    },
    getStatusType(status) {
      const typeMap = {
        0: 'info',
        1: 'success',
        2: 'danger'
      }
      return typeMap[status] || 'info'
    },
    statusClass(status) {
      const map = { 0: 's0', 1: 's1', 2: 's2' }
      return map[status] || 's0'
    },
    getStatusText(status) {
      const textMap = {
        0: '未发布',
        1: '已发布',
        2: '已失效'
      }
      return textMap[status] || '未知'
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
.code-tag {
  background: var(--ink-100);
  color: var(--ink-700);
  font-size: 12.5px;
  letter-spacing: 0.5px;
  padding: 3px 9px;
}

.bean-cell {
  font-size: 13px;
  color: var(--ink-900);
}

.bean-cell b {
  font-size: 15px;
  font-weight: 600;
  color: var(--rose-600);
}

.use-progress {
  font-size: 13px;
  color: var(--ink-900);
  line-height: 1.3;
}

.use-progress b {
  font-weight: 600;
}

.use-progress span {
  font-size: 12px;
  color: var(--ink-400);
}

.use-bar {
  margin-top: 5px;
  height: 4px;
  border-radius: 2px;
  background: var(--ink-100);
  overflow: hidden;
  max-width: 90px;
}

.use-bar i {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: var(--rose-400);
  transition: width 0.3s;
}

.users-pager {
  margin-top: 16px;
}

.status-select {
  width: 130px;
}

.form-hint {
  font-size: 12px;
  color: var(--ink-400);
  line-height: 1.6;
  margin-top: 4px;
}
</style>
